import { z } from 'zod';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { MainLayout } from '@/components/layout/MainLayout';
import { InfiniteScrollList } from '@/components/ui/infinite-scroll-list';
import { auditApi } from '@/lib/pillarApi';
import { type ListTransactionLogPageResponse, type TransactionLogListItem } from '@pillar-portal/api-types';

interface TransactionLogRowProps {
    readonly item: TransactionLogListItem;
}

function TransactionLogRow({ item }: TransactionLogRowProps) {
    return (
        <div className="border rounded-md p-3 bg-white space-y-2">
            <div className="min-w-0">
                <div className="text-sm font-semibold text-gray-900">
                    {item.table_name} {item.operation}
                </div>
                <div className="text-xs text-gray-600">
                    ts={item.timestamp} ref={item.transaction_log_ref}
                </div>
                <div className="text-xs text-gray-600 truncate">
                    ip={item.ip_address} ua={item.user_agent}
                </div>
            </div>

            <pre className="mt-2 text-xs bg-gray-50 p-3 rounded border overflow-x-auto max-h-96 overflow-y-auto">
                {JSON.stringify(item.table_row, null, 2)}
            </pre>
        </div>
    );
}

function AuditLogContent() {
    return (
        <InfiniteScrollList<TransactionLogListItem, string, readonly ['auditLog']>
            pageTitle="Audit Log"
            cardTitle="TransactionLog"
            queryPlaceholder="Search audit log..."
            queryKeyPrefix={['auditLog']}
            pageSize={100}
            fetchPage={async (params) => {
                const response: ListTransactionLogPageResponse = await auditApi.listTransactionLogPage({
                    limit: params.limit,
                    cursor: params.cursor,
                    ...(params.terms !== undefined ? { terms: [...params.terms] } : {}),
                });
                return { items: response.transaction_log, pagination: response.pagination };
            }}
            parseCursorParam={(pageParam: unknown) => {
                if (pageParam === null || pageParam === undefined) {
                    return undefined;
                }
                const parseResult = z.string().min(1).safeParse(pageParam);
                if (!parseResult.success) {
                    throw new Error('Expected string cursor');
                }
                return parseResult.data;
            }}
            stateCopy={{
                loadingLabel: 'Loading audit log...',
                emptyLabel: 'No audit log rows found.',
                endOfListLabel: 'End of audit log.',
                genericErrorLabel: 'Failed to load audit log',
            }}
            renderItems={({ items }) => (
                <div className="space-y-6">
                    <p className="text-sm text-gray-600">
                        Transaction log rows in descending recency order.
                    </p>
                    <div className="space-y-3">
                        {items.map((item) => (
                            <TransactionLogRow key={item.transaction_log_ref} item={item} />
                        ))}
                    </div>
                </div>
            )}
        />
    );
}

export default function AuditLogPage() {
    return (
        <ProtectedRoute>
            <MainLayout>
                <AuditLogContent />
            </MainLayout>
        </ProtectedRoute>
    );
}
